import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Snackbar,
  TextareaAutosize,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import DynamicTable from "../../../Common/CommonDynamicTable";
import styles from "../../../../styles/brsr/general.module.css";
import ReusableTable from "../../../Common/ReusableTable";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { get, put } from "../../../../utils/api";
import style from "../../../../styles/brsr/general.module.css";
import { useParams } from "react-router-dom";
import { brsrReview, reviewBRSR } from "../../../../utils/companyId";
import ReviewBox from "../../../Common/ReviewBox";
import { currentYear } from "../../../../utils/generateYearRanges";
import DeleteRowDialog from "../../../Common/DeleteRowDialog";
import BRSRFile from "../../../Common/BRSRFile";
import EastIcon from "@mui/icons-material/East";
import useReportingYearLabels from "../../../BRSRReport/SectionCPrinciples/useReportingYearLabels";

const LOCATION_ROW_LABELS = {
  rural: "Rural",
  semiUrban: "Semi-Urban",
  urban: "Urban",
  metropolitan: "Metropolitan",
  smallProducers: "Directly sourced from MSMEs/ small producers",
  neighbouringDistricts:
    "Sourced directly from within the district and neighbouring districts",
};

const FY_FIELD_LABELS = {
  currentFinacialYear: "current financial year (%)",
  previousFinancialYear: "previous financial year (%)",
};

const isEmptyFieldValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  return false;
};

const defaultJobCreationInSmallerTowns = {
  rural: { currentFinacialYear: "", previousFinancialYear: "" },
  semiUrban: { currentFinacialYear: "", previousFinancialYear: "" },
  urban: { currentFinacialYear: "", previousFinancialYear: "" },
  metropolitan: { currentFinacialYear: "", previousFinancialYear: "" },
};

const mergeJobCreationInSmallerTowns = (fromApi) => {
  const src = fromApi && typeof fromApi === "object" ? fromApi : {};
  const keys = ["rural", "semiUrban", "urban", "metropolitan"];
  const out = { ...defaultJobCreationInSmallerTowns };
  const pickStr = (v) => {
    if (v == null || v === "") return "";
    return String(v).trim();
  };
  keys.forEach((k) => {
    const row = src[k] && typeof src[k] === "object" ? src[k] : {};
    const current =
      row.currentFinacialYear ??
      row.currentFinancialYear ??
      row.current_financial_year ??
      "";
    const previous =
      row.previousFinancialYear ??
      row.previousFinacialYear ??
      row.previous_financial_year ??
      "";
    out[k] = {
      currentFinacialYear: pickStr(current),
      previousFinancialYear: pickStr(previous),
    };
  });
  return out;
};

const isApplicableYes = (value) => value === "Yes" || value === true;

const emptySocialImpactRow = {
  projectName: "",
  SIANotificationNo: "",
  dateOfNotification: "",
  conductedByExternalAgency: false,
  resultsCommunicatedPublicDomain: false,
  webLink: "",
};

const emptyRehabilitationRow = {
  projectName: "",
  state: "",
  district: "",
  numberOfProjectAffectedFamilies: "",
  percentagePAFsCoveredByRR: "",
  amountsPaidToPAFs: "",
};

const toApplicableLabel = (value) =>
  value === false || value === "No" ? "No" : "Yes";

const EssentialIndicator = ({
  onSave,
  hide,
  removeUpload = false,
  downloadMode = false,
}) => {
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState({ index: null, table: null });
  const [modalupload, setModalUpload] = useState({
    socialImpactAssessmentsDoc: false,
    rehabilitationAndResettlementDoc: false,
    communityGrievanceMechanismsDoc: false,
    sourceFromSupplierDoc: false,
    jobCreationInSmallerTownsDoc: false,
  });
  const [formData, setFormData] = useState({
    siaEnabled: "Yes",
    rnrEnabled: "Yes",
    socialImpactAssessments: [
      {
        // serialNumber: 1,
        ...emptySocialImpactRow,
      },
    ],
    rehabilitationAndResettlement: [
      {
        // serialNumber: 1,
        ...emptyRehabilitationRow,
      },
    ],
    communityGrievanceMechanisms: "",
    sourceFromSupplier: {
      smallProducers: { currentFinacialYear: "", previousFinancialYear: "" },
      neighbouringDistricts: {
        currentFinacialYear: "",
        previousFinancialYear: "",
      },
    },
    jobCreationInSmallerTowns: {
      rural: { currentFinacialYear: "", previousFinancialYear: "" },
      semiUrban: { currentFinacialYear: "", previousFinancialYear: "" },
      urban: { currentFinacialYear: "", previousFinancialYear: "" },
      metropolitan: { currentFinacialYear: "", previousFinancialYear: "" },
    },
    socialImpactAssessmentsDoc: [],
    rehabilitationAndResettlementDoc: [],
    communityGrievanceMechanismsDoc: [],
    sourceFromSupplierDoc: [],
    jobCreationInSmallerTownsDoc: [],
  });
  const [rows, setRows] = useState("");
  const [errors, setErrors] = useState([]);
  const [validationSnackbar, setValidationSnackbar] = useState({
    open: false,
    message: "",
  });
  const { currentYear, previousYear } = useReportingYearLabels();

  const jobCreationColumnLabels = useMemo(
    () => [
      { label: "Location", field: "Location", type: "disabled" },
      {
        label: currentYear
          ? `FY ${currentYear} (Current Financial Year)`
          : "Current Financial Year",
        field: "currentFinacialYear",
        type: "percentage",
        placeholder: "Enter Percentage",
      },
      {
        label: previousYear
          ? `FY ${previousYear} (Previous Financial Year)`
          : "Previous Financial Year",
        field: "previousFinancialYear",
        type: "percentage",
        placeholder: "Enter Percentage",
      },
    ],
    [currentYear, previousYear],
  );

  const handleDeleteClick = (rowIndex, tableField) => {
    const rowData = formData[tableField][rowIndex];

    // Ignore serialNumber when checking for filled data
    const hasData = Object.entries(rowData).some(
      ([key, val]) =>
        key !== "serialNumber" && val !== "" && val !== null && val !== false,
    );

    if (hasData) {
      setRowToDelete({ index: rowIndex, table: tableField });
      setOpenDialog(true);
    } else {
      // If only serialNumber is filled or all fields are empty, delete directly
      deleteRow(rowIndex, tableField);
    }
  };

  const deleteRow = (rowIndex, tableField) => {
    setFormData((prev) => {
      const updatedTable = [...prev[tableField]];
      updatedTable.splice(rowIndex, 1);
      const updatedTableWithSerialNumbers = updatedTable.map((row, index) => ({
        ...row,
        serialNumber: index + 1,
      }));
      return { ...prev, [tableField]: updatedTableWithSerialNumbers };
    });
  };

  const confirmDelete = () => {
    if (rowToDelete.index !== null && rowToDelete.table) {
      deleteRow(rowToDelete.index, rowToDelete.table);
    }
    setOpenDialog(false);
    setRowToDelete({ index: null, table: null });
  };

  const cancelDelete = () => {
    setOpenDialog(false);
    setRowToDelete({ index: null, table: null });
  };

  const validateForm = () => {
    let newErrors = {};

    // ✅ Check if formData exists to avoid undefined errors
    if (!formData) return false;
    if (isApplicableYes(formData.siaEnabled)) {
      formData.socialImpactAssessments.forEach((assessment, rowIndex) => {
        Object.entries(assessment).forEach(([key, value]) => {
          if (value === "" || value === null) {
            if (!newErrors.socialImpactAssessments)
              newErrors.socialImpactAssessments = [];
            newErrors.socialImpactAssessments[rowIndex] = {
              ...(newErrors.socialImpactAssessments[rowIndex] || {}),
              [key]: ` ${key}`, // ✅ Includes Row Number
              // [key]: `Row ${rowIndex + 1}: ${key}`,
            };
          }
        });
      });
    }

    // ✅ Validate `rehabilitationAndResettlement`
    if (isApplicableYes(formData.rnrEnabled)) {
      formData.rehabilitationAndResettlement.forEach((assessment, rowIndex) => {
        Object.entries(assessment).forEach(([key, value]) => {
          if (value === "" || value === null) {
            if (!newErrors.rehabilitationAndResettlement)
              newErrors.rehabilitationAndResettlement = [];
            newErrors.rehabilitationAndResettlement[rowIndex] = {
              ...(newErrors.rehabilitationAndResettlement[rowIndex] || {}),
              [key]: ` ${key}`, // ✅ Includes Row Number
              // [key]: `Row ${rowIndex + 1}: ${key}`,
            };
          }
        });
      });
    }

    // ✅ Validate `communityGrievanceMechanisms`
    if (formData.communityGrievanceMechanisms === "") {
      newErrors.communityGrievanceMechanisms = "This field is required";
    }

    // ✅ Validate `sourceFromSupplier`
    newErrors.sourceFromSupplier = validateNestedObject(
      formData.sourceFromSupplier,
    );

    // ✅ Validate `jobCreationInSmallerTowns` (all location % fields required)
    newErrors.jobCreationInSmallerTowns = validateNestedObject(
      formData.jobCreationInSmallerTowns,
    );

    // ✅ Remove empty error objects
    newErrors = Object.fromEntries(
      Object.entries(newErrors).filter(
        ([_, value]) => value && Object.keys(value).length > 0,
      ),
    );

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Nested tables: sourceFromSupplier & jobCreationInSmallerTowns (FY columns required)
  const validateNestedObject = (data) => {
    const err = {};

    if (!data || typeof data !== "object") return err;

    Object.keys(data).forEach((location) => {
      const jobData = data[location] || {};
      const missingFields = [];

      if (!err[location]) err[location] = {};

      Object.entries(jobData).forEach(([field, value]) => {
        if (field === "errorSummary") return;
        if (isEmptyFieldValue(value)) {
          missingFields.push(field);
          err[location][field] =
            `${FY_FIELD_LABELS[field] || field} is required`;
        }
      });

      if (missingFields.length > 0) {
        const labelLoc = LOCATION_ROW_LABELS[location] || location;
        const missingLabels = missingFields.map((f) => FY_FIELD_LABELS[f] || f);
        err[location].errorSummary =
          `${labelLoc}: please enter ${missingLabels.join(" and ")}`;
      }

      if (Object.keys(err[location]).length === 0) {
        delete err[location];
      }
    });

    return err;
  };

  const fetchData = async () => {
    const res = await get(
      `/brsr/get?subType=principle8&type=SectionC&id=${id}`,
    );

    const res1 =
      res?.data?.data?.BRSRSectionC?.principle8?.essentialIndicators ?? {};

    if (
      res?.data?.data?.year
        ? res?.data?.data?.year < currentYear - 1
        : false || !brsrReview
    )
      setDisabled(true);

    setFormData((prev) => ({
      ...prev,
      ...res1,
      siaEnabled: toApplicableLabel(res1?.siaEnabled),
      rnrEnabled: toApplicableLabel(res1?.rnrEnabled),
      socialImpactAssessments:
        res1?.socialImpactAssessments?.length > 0
          ? res1?.socialImpactAssessments
          : prev?.socialImpactAssessments,
      rehabilitationAndResettlement:
        res1?.rehabilitationAndResettlement?.length > 0
          ? res1?.rehabilitationAndResettlement
          : prev?.rehabilitationAndResettlement,
      socialImpactAssessmentsDoc: res1?.socialImpactAssessmentsDoc || [],
      rehabilitationAndResettlementDoc:
        res1?.rehabilitationAndResettlementDoc || [],
      communityGrievanceMechanismsDoc:
        res1?.communityGrievanceMechanismsDoc || [],
      sourceFromSupplierDoc: res1?.sourceFromSupplierDoc || [],
      jobCreationInSmallerTownsDoc: res1?.jobCreationInSmallerTownsDoc || [],
      jobCreationInSmallerTowns: mergeJobCreationInSmallerTowns(
        res1?.jobCreationInSmallerTowns,
      ),
    }));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleTableChange = (tableField, rowIndex, key, value, type) => {
    if (type === "number" && !/^\d*$/.test(value)) return;

    // if ( type === "percentage" &&
    //   !/^\d*$/.test(value) &&
    //   (Number(value) < 0 || Number(value) > 100)
    // ) return;
    if (type === "percentage") {
      // Allow only numbers and optional decimals
      if (!/^\d*\.?\d*$/.test(value)) return;

      // Convert value to a number and restrict range
      if (Number(value) < 0 || Number(value) > 100) return;
    }
    if (type === "date") {
    }
    setFormData((prevState) => {
      const updatedTable = [...prevState[tableField]];
      updatedTable[rowIndex] = { ...updatedTable[rowIndex], [key]: value };
      return { ...prevState, [tableField]: updatedTable };
    });
    if (value !== "" && value !== null) {
      clearCellError(tableField, rowIndex, key);
    }
  };

  const addRowToTable = (tableField, defaultRow) => {
    setFormData((prevState) => ({
      ...prevState,
      [tableField]: [...prevState[tableField], defaultRow],
    }));
  };

  const socialImpactColumns = [
    { label: "S.No", field: "serialNumber", type: "static" },
    {
      label: "Name and brief details of project",
      field: "projectName",
      type: "text",
    },
    { label: "SIA Notification No.", field: "SIANotificationNo", type: "text" },
    {
      label: `Date of notification, dd/mm/yyyy`,
      field: "dateOfNotification",
      type: "date",
    },
    {
      label: "Whether conducted by independent external agency . (Yes / No)",
      field: "conductedByExternalAgency",
      type: "select",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    {
      label: "Results communicated in public domain (Yes / No)",
      field: "resultsCommunicatedPublicDomain",
      type: "select",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    { label: "Relevant Web link", field: "webLink", type: "text" },
  ];

  const rehabilitationColumns = [
    { label: "S.No", field: "serialNumber", type: "static" },
    { label: "Project Name", field: "projectName", type: "text" },
    { label: "State", field: "state", type: "text" },
    { label: "District", field: "district", type: "text" },
    {
      label: "No. of Project Affected Families (PAFs)",
      field: "numberOfProjectAffectedFamilies",
      type: "number",
    },
    {
      label: "% of PAFs Covered by R&R",
      field: "percentagePAFsCoveredByRR",
      type: "percentage",
    },
    {
      label: "Amounts Paid to PAFs",
      field: "amountsPaidToPAFs",
      type: "number",
    },
  ];

  /* EssentialIndicator.jsx */

  const clearCellError = (tableField, rowKey, columnKey) => {
    setErrors((prev) => {
      if (!prev?.[tableField]?.[rowKey]?.[columnKey]) return prev;

      const copy = JSON.parse(JSON.stringify(prev)); // Deep clone

      // Remove the specific field error
      delete copy[tableField][rowKey][columnKey];

      const row = copy[tableField][rowKey];

      // Rebuild or drop summary from remaining field-level errors
      if (row?.errorSummary) {
        const fieldKeys = Object.keys(row).filter((k) => k !== "errorSummary");
        if (fieldKeys.length === 0) {
          delete copy[tableField][rowKey].errorSummary;
        } else {
          const labelLoc = LOCATION_ROW_LABELS[rowKey] || rowKey;
          const missingLabels = fieldKeys.map((f) => FY_FIELD_LABELS[f] || f);
          copy[tableField][rowKey].errorSummary =
            `${labelLoc}: please enter ${missingLabels.join(" and ")}`;
        }
      }

      // If no more errors in the row, delete the row
      if (Object.keys(copy[tableField][rowKey]).length === 0) {
        delete copy[tableField][rowKey];
      }

      // If no more errors in the table, delete the tableField
      if (Object.keys(copy[tableField]).length === 0) {
        delete copy[tableField];
      }

      return copy;
    });
  };

  const handleApplicableChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };

      if (value === "No") {
        if (field === "siaEnabled") {
          next.socialImpactAssessments = [
            { serialNumber: 1, ...emptySocialImpactRow },
          ];
        }
        if (field === "rnrEnabled") {
          next.rehabilitationAndResettlement = [
            { serialNumber: 1, ...emptyRehabilitationRow },
          ];
        }
      }

      return next;
    });

    setErrors((prev) => {
      const next = { ...prev };
      if (field === "siaEnabled") {
        delete next.socialImpactAssessments;
      }
      if (field === "rnrEnabled") {
        delete next.rehabilitationAndResettlement;
      }
      return next;
    });
  };

  const handleChange = (section, type, key, field, value) => {
    if (type === "percentage") {
      if (
        !/^\d*\.?\d*$/.test(value) ||
        Number(value) < 0 ||
        Number(value) > 100
      )
        return;
    }
    if (type === "number") {
      if (!/^\d*\.?\d*$/.test(value)) return;
    }
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: {
          ...prev[section][key],
          [field]:
            type === "percentage" || type === "number" ? Number(value) : value,
        },
      },
    }));
    // 2. clear the corresponding error if the new value is valid
    if (value !== "" && value !== null) {
      clearCellError(section, key, field);
    }
  };

  /** Q5: required % fields; store strings; 0–100 inclusive. */
  const handleJobCreationChange = (rowIndex, _type, field, value) => {
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    if (value !== "" && value !== ".") {
      const n = Number(value);
      if (Number.isNaN(n) || n < 0 || n > 100) return;
    }
    const keys = ["rural", "semiUrban", "urban", "metropolitan"];
    const key = keys[rowIndex];
    if (!key) return;

    setFormData((prev) => ({
      ...prev,
      jobCreationInSmallerTowns: {
        ...prev.jobCreationInSmallerTowns,
        [key]: {
          ...prev.jobCreationInSmallerTowns[key],
          [field]: value === "" ? "" : String(value),
        },
      },
    }));
    if (value !== "" && value !== null) {
      clearCellError("jobCreationInSmallerTowns", key, field);
    }
  };

  // Table configurations
  const supplierRows = [
    {
      label: "Directly sourced from MSMEs/ small producers",
      key: "smallProducers",
    },
    {
      label:
        "Sourced directly from within the district and neighbouring districts",
      key: "neighbouringDistricts",
    },
  ].map((item) => ({
    Location: item.label,
    currentFinacialYear:
      formData.sourceFromSupplier[item.key].currentFinacialYear,
    previousFinancialYear:
      formData.sourceFromSupplier[item.key].previousFinancialYear,
  }));

  const jobRows = ["rural", "semiUrban", "urban", "metropolitan"].map(
    (key) => ({
      Location:
        key === "semiUrban"
          ? "Semi-Urban"
          : key === "metropolitan"
            ? "Metropolitan"
            : key.charAt(0).toUpperCase() + key.slice(1),
      currentFinacialYear:
        formData.jobCreationInSmallerTowns[key]?.currentFinacialYear ?? "",
      previousFinancialYear:
        formData.jobCreationInSmallerTowns[key]?.previousFinancialYear ?? "",
    }),
  );

  const supplierColumns = [
    { label: "Location", field: "Location", type: "disabled" },
    {
      label: "Current financial year",
      field: "currentFinacialYear",
      type: "number",
    },
    {
      label: "Previous financial year",
      field: "previousFinancialYear",
      type: "number",
    },
  ];

  console.log("formdata", formData);
  const handleSubmit = async () => {
    if (!disabled) {
      if (!validateForm()) {
        setValidationSnackbar({
          open: true,
          message:
            "Please complete all required fields. See the messages below questions 4 and 5.",
        });
        return;
      }
      setValidationSnackbar({ open: false, message: "" });
      const jobCreationInSmallerTowns = mergeJobCreationInSmallerTowns(
        formData.jobCreationInSmallerTowns,
      );
      const payload = {
        essentialIndicators: {
          ...formData,
          siaEnabled: formData.siaEnabled === "Yes",
          rnrEnabled: formData.rnrEnabled === "Yes",
          socialImpactAssessments: isApplicableYes(formData.siaEnabled)
            ? formData.socialImpactAssessments
            : [],
          rehabilitationAndResettlement: isApplicableYes(formData.rnrEnabled)
            ? formData.rehabilitationAndResettlement
            : [],
          jobCreationInSmallerTowns,
          jobCreationInSmallerTownsDoc: Array.isArray(
            formData.jobCreationInSmallerTownsDoc,
          )
            ? formData.jobCreationInSmallerTownsDoc
            : [],
        },
        essentialIndicatorsSubmitted: true,
        type: "SectionC",
        subType: "principle8",
      };
      await put(`/brsr/update?id=${id}`, payload)
        .then((res) => onSave())
        .catch((err) => {});
    } else onSave();
  };
  return (
    <>
      <Snackbar
        open={validationSnackbar.open}
        autoHideDuration={8000}
        onClose={() => setValidationSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setValidationSnackbar((s) => ({ ...s, open: false }))}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {validationSnackbar.message}
        </Alert>
      </Snackbar>
      <DeleteRowDialog
        openDialog={openDialog}
        cancelRemoveRow={cancelDelete}
        confirmRemoveRow={confirmDelete}
      />
      <Box>
        {/* 1. Social Impact Assessments */}
        <p className={style.subHeading}>I. ESSENTIAL INDICATOR</p>
        <Box sx={{ mb: 3 }}>
          <p className={styles.question_title}>
            1. Details of Social Impact Assessments (SIA) of projects undertaken
            by the entity based on applicable laws, in the current financial
            year.
          </p>
          <RadioGroup
            row
            disabled={disabled}
            value={formData.siaEnabled}
            onChange={(e) =>
              handleApplicableChange("siaEnabled", e.target.value)
            }
            sx={{
              marginLeft: "1.25rem",
              marginTop: "-0.5rem",
              marginBottom: "0.5rem",
            }}
          >
            <FormControlLabel
              value={"Yes"}
              control={<Radio disabled={disabled} />}
              label="Applicable"
            />
            <FormControlLabel
              value={"No"}
              control={<Radio disabled={disabled} />}
              label="Not Applicable"
            />
          </RadioGroup>
          {isApplicableYes(formData.siaEnabled) && (
            <Box sx={{ ml: 2 }}>
              <DynamicTable
                disabled={disabled}
                hide={hide}
                data={formData.socialImpactAssessments}
                columns={socialImpactColumns}
                setRows={setRows}
                errors={errors?.socialImpactAssessments}
                // onDeleteRow={handleRemoveSocialImpactRow}
                onDeleteRow={(rowIndex) =>
                  handleDeleteClick(rowIndex, "socialImpactAssessments")
                }
                onRowChange={(rowIndex, key, value, type) =>
                  handleTableChange(
                    "socialImpactAssessments",
                    rowIndex,
                    key,
                    value,
                    type,
                  )
                }
                onAddRow={(defaultRow) =>
                  addRowToTable("socialImpactAssessments", defaultRow)
                }
                defaultRow={{
                  serialNumber: formData.socialImpactAssessments.length + 1,
                  ...emptySocialImpactRow,
                }}
              />
            </Box>
          )}
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "socialImpactAssessmentsDoc",
            ml: 2,
            removeUpload,
          }}
        />
        {/* 2. Rehabilitation and Resettlement */}
        <Box sx={{ mb: 3 }}>
          <p className={styles.question_title}>
            2. Provide information on project(s) for which ongoing
            Rehabilitation and Resettlement (R&R) is being undertaken by your
            entity, in the following format:
          </p>
          <RadioGroup
            row
            disabled={disabled}
            value={formData.rnrEnabled}
            onChange={(e) =>
              handleApplicableChange("rnrEnabled", e.target.value)
            }
            sx={{
              marginLeft: "1.25rem",
              marginTop: "-0.5rem",
              marginBottom: "0.5rem",
            }}
          >
            <FormControlLabel
              value={"Yes"}
              control={<Radio disabled={disabled} />}
              label="Applicable"
            />
            <FormControlLabel
              value={"No"}
              control={<Radio disabled={disabled} />}
              label="Not Applicable"
            />
          </RadioGroup>
          {isApplicableYes(formData.rnrEnabled) && (
            <Box sx={{ ml: 2 }}>
              <DynamicTable
                disabled={disabled}
                hide={hide}
                data={formData.rehabilitationAndResettlement}
                columns={rehabilitationColumns}
                setRows={setRows}
                errors={errors?.rehabilitationAndResettlement}
                // onDeleteRow={handleRemoveRow}
                // onDeleteRow={handleRemoveRehabilitationRow}
                onDeleteRow={(rowIndex) =>
                  handleDeleteClick(rowIndex, "rehabilitationAndResettlement")
                }
                onRowChange={(rowIndex, key, value, type) =>
                  handleTableChange(
                    "rehabilitationAndResettlement",
                    rowIndex,
                    key,
                    value,
                    type,
                  )
                }
                onAddRow={(defaultRow) =>
                  addRowToTable("rehabilitationAndResettlement", defaultRow)
                }
                defaultRow={{
                  serialNumber:
                    formData.rehabilitationAndResettlement.length + 1,
                  ...emptyRehabilitationRow,
                }}
              />
            </Box>
          )}
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "rehabilitationAndResettlementDoc",
            ml: 2,
            removeUpload,
          }}
        />
        {/* 3. Community Grievance Mechanisms */}
        <Box sx={{ mb: 3 }}>
          <p className={styles.question_title}>
            3. Describe the mechanisms to receive and redress grievances of the
            community.
          </p>
          <TextareaAutosize
            disabled={disabled}
            minRows={3}
            style={{
              width: "98%",
              padding: "8px",
              background: "transparent",
              border: "1px solid #74757E",
              borderRadius: "4px",
              marginLeft: "1rem",
            }}
            error={!!(errors["communityGrievanceMechanisms"] && true)}
            placeholder="Type Here"
            value={formData.communityGrievanceMechanisms}
            onChange={(e) =>
              setFormData({
                ...formData,
                communityGrievanceMechanisms: e.target.value,
              })
            }
            InputLabelProps={{
              style: {
                color: errors["communityGrievanceMechanisms"]
                  ? "#D34040"
                  : undefined,
              },
            }}
          />
          <p
            color="error"
            variant="caption"
            style={{
              lineHeight: "1.2",
              margin: "2px 0",
              // marginTop: "5px",
              color: "#d34040",
              fontSize: "10px",
              fontStyle: "italic",
              marginLeft: "15px",
            }}
          >
            {errors["communityGrievanceMechanisms"]}
          </p>
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "communityGrievanceMechanismsDoc",
            ml: 2,
            removeUpload,
          }}
        />
        {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
        {/* 4. Percentage of input material (inputs to total inputs by value) sourced from suppliers: */}
        <Box>
          <ReusableTable
            disabled={disabled}
            title="4. Percentage of input material (inputs to total inputs by value) sourced from suppliers:"
            rows={supplierRows}
            columns={supplierColumns}
            data={formData.sourceFromSupplier}
            errors={errors?.sourceFromSupplier}
            setRows={setRows}
            // errors={errors?.sourceFromSupplier}

            onChange={(rowIndex, type, field, value) =>
              handleChange(
                "sourceFromSupplier",
                (type = "percentage"),
                Object.keys(formData.sourceFromSupplier)[rowIndex],
                field,
                value,
              )
            }
          />
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "sourceFromSupplierDoc",
            ml: 2,
            removeUpload,
          }}
        />
        {/* 5. Job creation in smaller towns */}
        <Box sx={{ mb: 3 }}>
          <p className={styles.question_title}>
            5. Job creation in smaller towns – Disclose wages paid to persons
            employed (including employees or workers employed on a permanent or
            non-permanent / on contract basis) in the following locations, as %
            of total wage cost
          </p>
          <ReusableTable
            disabled={disabled}
            rows={jobRows}
            columns={jobCreationColumnLabels}
            data={formData.jobCreationInSmallerTowns}
            errors={errors?.jobCreationInSmallerTowns}
            setRows={setRows}
            onChange={handleJobCreationChange}
          />
          <Typography
            component="p"
            variant="body2"
            sx={{
              ml: 2,
              width: "98.5%",
              boxSizing: "border-box",
              mt: 1,
              color: "#000000",
              fontSize: "13px",
              lineHeight: 1.45,
              fontFamily: "Inter, sans-serif",
            }}
          >
            {`*(Place to be categorized as per RBI Classification System - rural / semi-urban / urban / metropolitan)`}
          </Typography>
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "jobCreationInSmallerTownsDoc",
            ml: 2,
            removeUpload,
          }}
        />
        {!hide && (
          <>
            {brsrReview ? (
              <Box sx={{ width: "98.5%", ml: 2 }}>
                <ReviewBox
                  url={`/brsr/review/${id}`}
                  handleSubmit={handleSubmit}
                />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default EssentialIndicator;
